/**
 * ResolvedPricing — the merged output of the cascade resolver.
 *
 * Promoted to a Schema.Class so it can serialise into the
 * `billable_items.resolved_pricing_snapshot` JSONB column and audit log
 * payloads, while still being a first-class in-memory value.
 *
 * Field provenance (regionPricingVersionId / networkPricingVersionId /
 * orgPricingVersionId / resolverVersion) is populated by the resolver, not by
 * any single custom_pricing_versions row.
 *
 * Part of the billing bounded context (§5.1 of the unified-pricing spec).
 */
import { Array as Arr, Option, Schema as S } from "effect"
import { BaseTierEntry } from "./base-tier-pricing"
import { AddonKey } from "@/domain/addon/organization-addon"

// =============================================================================
// Resolved addon price (one row per addon overlayed onto the final result)
// =============================================================================

export class ResolvedAddonPrice extends S.Class<ResolvedAddonPrice>("ResolvedAddonPrice")({
  addonKey: AddonKey,
  priceCents: S.Int.pipe(S.nonNegative())
}) {}

// =============================================================================
// ResolvedPricing — final cascade output
// =============================================================================

export class ResolvedPricing extends S.Class<ResolvedPricing>("ResolvedPricing")({
  baseTiers: S.NonEmptyArray(BaseTierEntry),
  proPricePerSeatCents: S.Int.pipe(S.nonNegative()),
  maxPricePerSeatCents: S.Int.pipe(S.nonNegative()),
  addons: S.Array(ResolvedAddonPrice),
  annualDiscountRate: S.Number.pipe(S.between(0, 1)),

  // provenance — populated by the resolver, NOT by the version row
  regionPricingVersionId: S.NullOr(S.UUID),
  networkPricingVersionId: S.NullOr(S.UUID),
  orgPricingVersionId: S.NullOr(S.UUID),
  resolverVersion: S.String,

  // labels for UI provenance display
  networkName: S.NullOr(S.String),
  // Network entity id (NOT the pricing-version id) for stable cross-system
  // referencing in Stripe metadata, audit logs, and admin views. Populated
  // only when the active pricing pin is the network layer.
  networkId: S.NullOr(S.UUID),

  // Deal fields — sourced from whichever pinned layer's version row carries
  // them (per spec §G). Stripe coupon is attached at pin time; expiry is
  // handled by the Stripe webhook.
  dealPercentOff: S.NullOr(S.Int.pipe(S.between(1, 100))),
  dealDurationMonths: S.NullOr(S.Int.pipe(S.between(1, 24))),
  dealStripeCouponIdMonthly: S.NullOr(S.String),
  dealStripeCouponIdAnnual: S.NullOr(S.String),
  dealSourceVersionId: S.NullOr(S.UUID)
}) {}

// =============================================================================
// Active pin resolution — the topmost non-null pin in the cascade.
//
// Three call sites today need to answer "which layer actually drove this
// price?": Stripe metadata breadcrumbs (apply-plan-change /
// change-billing-account-plan-orchestrator), the providence label in
// plan-change-service, and the audit/drift reconciler payloads. Centralising
// the precedence (custom_org > network > region) keeps the answer
// consistent and means a future fourth layer is a one-line change here.
// =============================================================================

export type PricingLayer = "custom_org" | "network" | "region"

export interface ActivePricingPin {
  readonly layer: PricingLayer
  readonly versionId: string
}

/**
 * Return the topmost non-null pricing pin on a ResolvedPricing snapshot —
 * the one whose version row carried the final priced fields. Precedence is
 * fixed: custom_org > network > region (spec §F.2).
 *
 * Returns `null` ONLY when all three pins are null, which the DB CHECK
 * constraint `billing_accounts_pricing_pins_present` should make
 * unreachable for any account that has been through the resolver — but the
 * function is total so defensive callers don't crash on a pre-seed row.
 */
export const getActivePricingPin = (
  resolved: Pick<
    ResolvedPricing,
    "orgPricingVersionId" | "networkPricingVersionId" | "regionPricingVersionId"
  >
): ActivePricingPin | null => {
  if (resolved.orgPricingVersionId !== null) {
    return { layer: "custom_org", versionId: resolved.orgPricingVersionId }
  }
  if (resolved.networkPricingVersionId !== null) {
    return { layer: "network", versionId: resolved.networkPricingVersionId }
  }
  if (resolved.regionPricingVersionId !== null) {
    return { layer: "region", versionId: resolved.regionPricingVersionId }
  }
  return null
}

// =============================================================================
// Addon price lookup
//
// Whether a given addon is priced for an org is a RUNTIME data fact — it
// depends on which pricing version is active — so `S.Array(ResolvedAddonPrice)`
// cannot encode presence at the type level. We surface that partiality as an
// `Option` rather than letting callers fish with `.find() ?? 0`: the must-fail
// billing seam bridges `None` to a typed error, while display surfaces choose
// their own fallback (`Option.getOrElse(() => 0)`, "unavailable", etc.) in
// plain sight. A present `priceCents` of 0 is a legitimate free/promo price and
// resolves to `Some(0)`; only absence is `None`.
// =============================================================================

export const findAddonPriceCents = (
  resolved: Pick<ResolvedPricing, "addons">,
  addonKey: AddonKey
): Option.Option<number> =>
  Arr.findFirst(resolved.addons, (a) => a.addonKey === addonKey).pipe(
    Option.map((a) => a.priceCents)
  )
